import type { ReactNode } from 'react';

import type { PlatformToastDescriptionKind } from './types';

const EXPLANATORY_TERMS =
    /\b(?:are|can|could|did|does|failed|has|have|is|please|preparing|saved|should|started|unable|updated|was|were|will|would)\b/i;

const VALUE_PATTERNS = [
    /^(?:https?:\/\/|mailto:|\/)/i,
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    /^(?:[A-Z]{2,}:\s*)?[A-Z0-9._-]+$/,
    /\b\d{1,2}:\d{2}\s*(?:AM|PM)\b/i,
    /\b(?:ago|today|tomorrow|yesterday)\b/i,
];

export const isExplanatoryToastDescription = (description: string): boolean => {
    const value = description.trim();

    if (!value || VALUE_PATTERNS.some((pattern) => pattern.test(value))) {
        return false;
    }

    const words = value.split(/\s+/);
    const isTitleCaseLabel =
        words.length > 1 &&
        words.every((word) => /^[A-Z0-9]/.test(word) || /^(?:a|an|and|for|in|of|on|the|to|with)$/i.test(word));

    if (isTitleCaseLabel) {
        return false;
    }

    return words.length >= 6 || EXPLANATORY_TERMS.test(value);
};

export const normalizeToastDescription = (
    description: ReactNode,
    kind: PlatformToastDescriptionKind = 'auto',
): ReactNode => {
    if (typeof description !== 'string') {
        return description;
    }

    const value = description.trim();

    if (!value || kind === 'value' || /[.!?…]$/.test(value)) {
        return value;
    }

    const explanatory = kind === 'explanatory' || (kind === 'auto' && isExplanatoryToastDescription(value));

    return explanatory ? `${value}.` : value;
};
